import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSettings = ({ theme = 'dark' }) => {
  const { token } = useAuth();
  const [paymentMethod, setPaymentMethod] = useState('');
  const [accountIdentifier, setAccountIdentifier] = useState('');
  const [currentAccount, setCurrentAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPinPrompt, setShowPinPrompt] = useState(false);
  const [pin, setPin] = useState('');
  const [hasPin, setHasPin] = useState(true);

  const isLight = theme === 'light';

  useEffect(() => {
    fetchPaymentAccount();
    fetchPinStatus();
  }, []);

  const fetchPinStatus = async () => {
    try {
      const res = await axios.get(`${API}/security/pin/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasPin(res.data.has_pin);
    } catch (e) {
      // ignore
    }
  };

  const fetchPaymentAccount = async () => {
    try {
      const response = await axios.get(`${API}/settings/payment-account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentAccount(response.data);
      if (response.data.payment_method) {
        setPaymentMethod(response.data.payment_method);
        setAccountIdentifier(response.data.payment_account || '');
      }
    } catch (error) {
      console.error('Error fetching payment account:', error);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!paymentMethod || !accountIdentifier) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    if (!hasPin) {
      toast.error('Primero debes crear tu PIN de retiro en la sección "Seguridad de la Cuenta".');
      return;
    }
    // Require the withdrawal PIN before saving banking details
    setPin('');
    setShowPinPrompt(true);
  };

  const submitWithPin = async () => {
    if (!/^\d{6}$/.test(pin)) {
      toast.error('El PIN debe tener 6 dígitos');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/settings/payment-account`,
        {
          payment_method: paymentMethod,
          account_identifier: accountIdentifier,
          pin: pin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Cuenta de pago configurada correctamente');
      setShowPinPrompt(false);
      setPin('');
      fetchPaymentAccount();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'pin_not_set') {
        toast.error('Primero debes crear tu PIN de retiro en la sección "Seguridad de la Cuenta".');
        setShowPinPrompt(false);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Error al guardar la cuenta de pago');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className={isLight ? "bg-white rounded-2xl shadow-sm border border-gray-100" : "card-viubux-dark border-purple-500/20"} data-testid="payment-settings-card">
      <CardHeader>
        <CardTitle className={`gotham-bold flex items-center gap-2 ${isLight ? 'text-gray-900' : 'text-white'}`}>
          <CreditCard className="w-5 h-5 text-violet-500" />
          Configuración de Pagos
        </CardTitle>
        <CardDescription className={`verdana-regular ${isLight ? 'text-gray-500' : 'text-white/70'}`}>
          Configura tu cuenta para recibir pagos de comisiones
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Info sobre pagos */}
        <div className={`rounded-xl p-4 mb-4 ${isLight ? 'bg-blue-50 border border-blue-200' : 'bg-blue-500/10 border border-blue-500/30'}`}>
          <p className={`verdana-regular text-sm ${isLight ? 'text-blue-700' : 'text-blue-200'}`}>
            📅 <strong>Calendario de pagos:</strong> Los pagos de comisiones se procesan el <strong>último viernes de cada mes</strong>. Monto mínimo: <strong>$500 MXN</strong> (Audio-Lectores) / <strong>$2,000 MXN</strong> (Autores).
          </p>
        </div>
        
        {currentAccount?.payment_method ? (
          <div className={`rounded-xl p-4 mb-4 ${isLight ? 'bg-green-50 border border-green-200' : 'bg-green-500/10 border border-green-500/30'}`}>
            <p className={`verdana-regular text-sm mb-1 ${isLight ? 'text-green-700' : 'text-green-200'}`}>
              ✓ <strong>Cuenta configurada:</strong>
            </p>
            <p className={`verdana-regular text-sm ${isLight ? 'text-gray-900' : 'text-white'}`}>
              {currentAccount.payment_method.toUpperCase()}: {currentAccount.payment_account}
            </p>
          </div>
        ) : (
          <div className={`rounded-xl p-4 mb-4 ${isLight ? 'bg-amber-50 border border-amber-200' : 'bg-yellow-500/10 border border-yellow-500/30'}`}>
            <p className={`verdana-regular text-sm ${isLight ? 'text-amber-700' : 'text-yellow-200'}`}>
              ⚠️ <strong>Importante:</strong> Configura tu cuenta de pago para recibir tus comisiones.
            </p>
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className={`verdana-regular text-sm mb-2 block ${isLight ? 'text-gray-700' : 'text-white/80'}`}>
              Método de Pago
            </label>
            <select
              data-testid="payment-method-select"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className={`w-full px-4 py-3 rounded-xl verdana-regular focus:outline-none focus:ring-2 focus:ring-violet-500 ${
                isLight 
                  ? 'bg-white border border-gray-200 text-gray-900' 
                  : 'bg-black/30 border border-purple-500/30 text-white'
              }`}
              required
            >
              <option value="">Seleccionar método</option>
              <option value="spei">SPEI (CLABE Interbancaria)</option>
              <option value="payoneer">Payoneer</option>
              <option value="paypal">PayPal</option>
              <option value="stripe">Stripe</option>
            </select>
          </div>

          <div>
            <label className={`verdana-regular text-sm mb-2 block ${isLight ? 'text-gray-700' : 'text-white/80'}`}>
              {paymentMethod === 'paypal' ? 'Email de PayPal' : 
               paymentMethod === 'stripe' ? 'ID de Cuenta Stripe' : 
               paymentMethod === 'spei' ? 'CLABE Interbancaria (18 dígitos)' :
               paymentMethod === 'payoneer' ? 'Email de Payoneer' :
               'Identificador de Cuenta'}
            </label>
            <input
              data-testid="payment-account-input"
              type={paymentMethod === 'spei' ? 'text' : 'text'}
              value={accountIdentifier}
              onChange={(e) => setAccountIdentifier(e.target.value)}
              placeholder={
                paymentMethod === 'paypal' ? 'tu@email.com' : 
                paymentMethod === 'stripe' ? 'acct_xxxx' :
                paymentMethod === 'spei' ? '012345678901234567' :
                paymentMethod === 'payoneer' ? 'tu@email.com' :
                'Ingresa tu identificador'
              }
              pattern={paymentMethod === 'spei' ? '[0-9]{18}' : undefined}
              maxLength={paymentMethod === 'spei' ? 18 : undefined}
              className={`w-full px-4 py-3 rounded-xl verdana-regular focus:outline-none focus:ring-2 focus:ring-violet-500 ${
                isLight 
                  ? 'bg-white border border-gray-200 text-gray-900 placeholder-gray-400' 
                  : 'bg-black/30 border border-purple-500/30 text-white placeholder-white/40'
              }`}
              required
            />
            {paymentMethod === 'spei' && (
              <p className={`text-xs mt-1 ${isLight ? 'text-gray-500' : 'text-white/50'}`}>
                La CLABE debe tener exactamente 18 dígitos
              </p>
            )}
          </div>

          <Button
            data-testid="save-payment-account-btn"
            type="submit"
            className={`w-full rounded-xl ${isLight ? 'bg-violet-600 hover:bg-violet-700 text-white' : 'btn-viubux'}`}
            disabled={loading}
          >
            {loading ? 'Guardando...' : 'Guardar Cuenta de Pago'}
          </Button>
        </form>

        {!hasPin && (
          <div className={`rounded-xl p-3 mb-4 ${isLight ? 'bg-red-50 border border-red-200' : 'bg-red-500/10 border border-red-500/30'}`} data-testid="pin-required-warning">
            <p className={`verdana-regular text-xs ${isLight ? 'text-red-700' : 'text-red-300'}`}>
              🔒 <strong>Protección requerida:</strong> Crea tu PIN de retiro de 6 dígitos en la sección <strong>Seguridad de la Cuenta</strong> antes de configurar tus datos bancarios.
            </p>
          </div>
        )}

        <div className={`mt-4 rounded-xl p-3 ${isLight ? 'bg-gray-50' : 'bg-black/30'}`}>
          <p className={`verdana-regular text-xs ${isLight ? 'text-gray-500' : 'text-white/60'}`}>
            <strong>Nota:</strong> Tus comisiones se pagarán a la cuenta configurada. Cualquier cambio en tus datos bancarios requiere tu PIN de retiro de 6 dígitos.
          </p>
        </div>

        {showPinPrompt && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="banking-pin-modal">
            <div className="bg-[#1a0b2e] border border-purple-500/30 rounded-2xl p-6 w-full max-w-sm">
              <h3 className="gotham-bold text-white text-lg mb-2 flex items-center gap-2">
                <span>🔒</span> Confirma con tu PIN
              </h3>
              <p className="verdana-regular text-white/70 text-sm mb-4">
                Ingresa tu PIN de retiro de 6 dígitos para guardar los datos bancarios.
              </p>
              <input
                data-testid="banking-pin-input"
                type="password"
                inputMode="numeric"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                placeholder="••••••"
                className="w-full px-4 py-3 rounded-xl bg-black/30 border border-purple-500/30 text-white text-center text-2xl tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-violet-500 mb-4"
                autoFocus
              />
              <div className="flex gap-3">
                <Button
                  data-testid="banking-pin-cancel"
                  variant="outline"
                  onClick={() => { setShowPinPrompt(false); setPin(''); }}
                  className="flex-1 border-white/20 text-white/80 hover:bg-white/10"
                >
                  Cancelar
                </Button>
                <Button
                  data-testid="banking-pin-confirm"
                  onClick={submitWithPin}
                  disabled={loading}
                  className="flex-1 btn-viubux"
                >
                  {loading ? 'Guardando...' : 'Confirmar'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentSettings;
